import React, { memo, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import TourV2RouteMapModal from './TourV2RouteMapModal'
import useToggle from 'hooks/useToggle'
import Clickable from 'components/Common/Clickable'
import TourV2Itinerary from './TourV2Itinerary'
import LineFocusIcon from 'components/Luxkit/Icons/line/LineFocusIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { pushWithRegion } from 'actions/NavigationActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

const Root = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(30)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }

  > .itinerary-area {
    flex-grow: 1;
  }

  > .map-area {
    flex-shrink: 0;
  }
`

const RouteMapModalActivator = styled(Clickable)`
  display: flex;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(457)};
    max-height: ${rem(685)};
    position: sticky;
    top: ${rem(130)}; /* sticky header height (118) + margin (12) */
  }
`

const ExpandBadge = styled.div`
  display: flex;
  position: absolute;
  border-radius: ${props => props.theme.borderRadius.S};
  right: 0;
  bottom: 0;
  margin: ${rem(8)};
  gap: ${rem(8)};
  padding: ${rem(8)};
  background-color: rgba(0, 0, 0, .8);
`
interface Props {
  offerType: Tours.V2OfferType
  itinerary: Tours.V2OfferItineraryItem[]
  variation?: Tours.V2OfferVariation
  alternativeVariation?: Tours.V2OfferVariation
  onAlternativeRouteChange: (variationId: string) => void
}

interface MappedProps {
  baseTourOfferUrl: string
}

function TourV2ItineraryAndMap(props: Props & MappedProps) {
  const { itinerary, variation, offerType, alternativeVariation, onAlternativeRouteChange, baseTourOfferUrl } = props
  const routeMapImage = variation?.routeMapImage

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const changeAlternativeVariation = useCallback(() => {
    if (alternativeVariation) {
      offerPageDispatch({ type: OfferPageActions.unselectTourV2OfferDeparture })
      dispatch(pushWithRegion(
        `${baseTourOfferUrl}/${alternativeVariation.id}`,
        '',
        { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_CONTENT_TOP_ANCHOR' },
      ))
      offerPageDispatch({
        type: OfferPageActions.selectTourVOfferVariation,
        variation: alternativeVariation,
      })
      onAlternativeRouteChange(alternativeVariation.id)
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [offerPageDispatch, dispatch, baseTourOfferUrl, onAlternativeRouteChange, alternativeVariation],
  )

  const [mapModalOpen, , openMapModal, closeMapModal] = useToggle(false)
  return <>
    <Root>
      <div className="itinerary-area">
        <TourV2Itinerary
          itinerary={itinerary}
          title="What you'll see"
          offerType={offerType}
          alternativeVariation={alternativeVariation}
          changeAlternativeVariation={changeAlternativeVariation}
        />
      </div>

      {!!routeMapImage && <div className="map-area">
        <RouteMapModalActivator onClick={openMapModal}>
          <ResponsiveImage
            id={routeMapImage.id}
            alt={routeMapImage.title}
            tabletWidth="370px"
            desktopWidth="457px"
            mobileAspectRatio="1:1"
            tabletAspectRatio={undefined}
          />

          <ExpandBadge>
            <LineFocusIcon size="S" colour="neutral-eight" />
            <BodyText variant="medium" colour="neutral-eight">Expand map</BodyText>
          </ExpandBadge>
        </RouteMapModalActivator>
      </div>}

    </Root>

    {!!routeMapImage && <TourV2RouteMapModal
      open={mapModalOpen}
      onClose={closeMapModal}
      variation={variation}
    />}
  </>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
})

export default memo(withOfferPageState(mapOfferPageStateToProps)(TourV2ItineraryAndMap))
